<template>
  <div
    class="fixed flex items-center justify-center top-0 left-0 bottom-0 right-0 z-30 overflow-y-auto"
  >
    <div class="h-full w-full">
      <div class="rounded bg-white p-4 shadow-depth-300 mx-auto w-2/5 shadow-lg my-4">
        <div class="text-lg">
          <div class="float-left">
            {{ title }}
          </div>
          <div class="float-right">
            <font-awesome-icon
              :icon="['fal', 'times']"
              @click="$emit('close')"
            />
          </div>
        </div>
        <div class="flex w-full">
          <z-input
            v-model="name"
            :label="$t('Custom Label Name', 'Custom Label Name')"
            class="w-1/2 text-sm"
            tooltip="Write here the name of your custom label."
          />
          <project-dropdown
            :all-projects="allProjects"
            :selected-projects="selectedProjects"
            class="w-1/2 ml-2 h-5"
            @select="addProject"
            @deselect="removeProject"
          />
        </div>
        <div class="mt-5 text-sm">
          <div class="text-left ml-2 mb-2 flex">
            {{ $t('Synonyms include', 'Include at least one of') }}
            <v-popover
              trigger="hover"
              placement="bottom"
              class="m-0"
            >
              <font-awesome-icon
                class="text-black text-default font-normal ml-1 tooltip-target"
                :icon="['fal', 'question-circle']"
              />
              <template slot="popover">
                <ul class="tooltip-content text-xs list-disc">
                  <li> {{ $t('Synonyms include tooltip first', 'Add multiple phrases, even at the same time by pasting them from your clipboard. Just make sure the individual phrases are divided by a comma “,” character.') }} </li>
                  <li> {{ $t('Synonyms include tooltip second', 'Link together different words by putting “AND” or “&&” between the words. (eg. apple && pie or apple AND pie)') }} </li>
                  <li> {{ $t('Synonyms include tooltip third', 'If you don’t want to include any suffix for a word, you can use the “]” character for it. (eg. colour] will not include colourful)') }} </li>
                </ul>
              </template>
            </v-popover>
          </div>
          <tag-input-field
            :tag-list="synonymsList"
            :placeholder="synonymsPlaceholder"
            data-test-id="synonymsTagInput"
            @input="newSynonyms"
            @removeTag="removeSynonym"
          />
        </div>
        <div class="mt-5 text-sm">
          <div class="text-left ml-2 mb-2">
            {{ $t('Synonyms not include', 'Do not include') }}
            <font-awesome-icon
              v-tooltip.top="$t('Synonyms not include tooltip', 'Write here the excludes phrases according to your custom label. Separate them with commas. You can connect two phrases by adding <i>AND</i> or <i>;</i> between phrases.')"
              class="text-black text-default font-normal"
              :icon="['fal', 'question-circle']"
            />
          </div>
          <tag-input-field
            :tag-list="excludesList"
            :placeholder="excludesPlaceholder"
            data-test-id="excludesTagInput"
            @input="newExcludes"
            @removeTag="removeExclude"
          />
        </div>
        <div class="mt-5">
          <z-button
            :type="'primary'"
            :text="buttonText"
            @click="save"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import i18next from 'i18next'
import _ from 'lodash'
import Button from '../../global/ui/Button'
import Input from '../../global/ui/Input'
import TagInputField from '../../global/ui/TagInputField'
import { library } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faQuestionCircle, faTimes } from '@fortawesome/pro-light-svg-icons'
import { actionNames } from '@/custom-labels/store'
import ProjectDropdown from '@/global/ui/ProjectDropdown/ProjectDropdown'
library.add(faQuestionCircle, faTimes)

export default {
  components: {
    ProjectDropdown,
    FontAwesomeIcon,
    'z-input': Input,
    TagInputField,
    'z-button': Button
  },
  props: {
    customLabel: {
      type: Object,
      default: () => ({
        id: null,
        name: '',
        synonyms: '',
        excludes: '',
        synonymsList: [],
        excludesList: [],
        filtersList: [],
        projects: []
      })
    },
    title: { type: String, default: '' },
    type: { type: String, default: 'general' },
    buttonText: { type: String, default: () => i18next.t('Save', 'Save') },
    allProjects: { type: Array, default: () => ([]) }
  },
  data () {
    return {
      id: null,
      name: '',
      synonymsList: [],
      excludesList: [],
      selectedProjects: [],
      suggestionTarget: 'synonym'
    }
  },
  computed: {
    synonymsPlaceholder () {
      return Array.isArray(this.synonymsList) && this.synonymsList.length > 1 ? '' : this.$t('Synonym example placeholder', 'e.g.: Internet')
    },
    excludesPlaceholder () {
      return Array.isArray(this.excludesList) && this.excludesList.length > 1 ? '' : this.$t('Exclude example placeholder', 'e.g.: wifi')
    },
    synonyms () {
      return this.synonymsList.reduce((list, tag) => list ? `${list},${tag}` : tag, '')
    },
    excludes () {
      return this.excludesList.reduce((list, tag) => list ? `${list},${tag}` : tag, '')
    }
  },
  watch: {
    customLabel () {
      this.updateCustomLabel()
    }
  },
  mounted () {
    this.updateCustomLabel()
  },
  methods: {
    newSynonyms (words) {
      this.suggestionTarget = 'synonym'
      const uniqueWords = words.filter(synonym => this.synonymsList.indexOf(synonym) === -1)
      uniqueWords.forEach(word => this.addSynonym(word))
    },
    addSynonym (synonym) {
      this.synonymsList = _.uniq([...this.synonymsList, synonym.trim()]).filter(val => val !== '')
    },
    removeSynonym (synonymToDelete) {
      this.synonymsList = this.synonymsList.filter(synonym => synonym !== synonymToDelete)
    },
    newExcludes (words) {
      const uniqueWords = words.filter(synonym => this.excludesList.indexOf(synonym) === -1)
      uniqueWords.forEach(word => this.addExclude(word))
      this.suggestionTarget = 'exclude'
    },
    removeExclude (excludeToDelete) {
      this.excludesList = this.excludesList.filter(exclude => exclude !== excludeToDelete)
    },
    addExclude (exclude) {
      this.excludesList = _.uniq([...this.excludesList, exclude.trim()]).filter(val => val !== '')
    },
    addProject (p) {
      this.selectedProjects = [...this.selectedProjects, p]
    },
    removeProject (p) {
      this.selectedProjects = this.selectedProjects.filter(sp => sp.id !== p.id)
    },
    async save () {
      if (this.synonyms !== '') {
        if (!this.customLabel) {
          await this.$store.dispatch(actionNames.ADD_CUSTOM_LABEL, {
            name: this.name,
            synonyms: this.synonymsList,
            excludes: this.excludesList,
            projects: this.selectedProjects.map(sp => sp.id)
          })
        } else {
          await this.$store.dispatch(actionNames.UPDATE_CUSTOM_LABEL, {
            id: this.customLabel.id,
            customLabel: {
              name: this.name,
              synonyms: this.synonymsList,
              excludes: this.excludesList,
              projects: this.selectedProjects.map(sp => sp.id)
            }
          })
        }
        this.$emit('close')
      } else {
        this.$toast.error(this.$t('Custom label error', 'Synonyms are missing'))
      }
    },
    updateCustomLabel () {
      if (this.customLabel && this.customLabel instanceof Object) {
        this.id = this.customLabel.id || null
        this.name = this.customLabel.name || ''
        this.synonymsList = this.customLabel.synonyms || []
        this.excludesList = this.customLabel.excludes || []
        this.selectedProjects = this.customLabel.projects || []
      }
    }
  }
}
</script>
